import { styled } from '@mui/material';
import { MouseEvent, useMemo } from 'react';
import { IChatConversation } from 'src/@types/chat';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import Iconify from 'src/components/iconify';
import { NavActionButton } from 'src/features/Navbar/NavActionButton/NavActionButton';
import { NavItem } from 'src/features/Navbar/NavItem';
import { P2PChannelAvatar } from 'src/features/channel/P2PChannelAvatar';
import { useWorkspaceParticipantsUsers } from 'src/hooks/useWorkspace';
import { useVoiceChannelsMembers } from 'src/provider/VoiceChannel';
import {
  ChatDetailsTitle,
  useChatDetailsDialog,
} from 'src/sections/@dashboard/chat/shared/ChatOverviewDialog';

type Props = {
  conversation: IChatConversation;
};

export const ChatNavItemP2P = ({ conversation }: Props) => {
  const path = PATH_DASHBOARD.chat.view(conversation._id);

  const { user } = useAuthContext();

  const { showChatDetailsDialog } = useChatDetailsDialog();

  const { voiceMembers } = useVoiceChannelsMembers(conversation);

  const handleShowDetails = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    showChatDetailsDialog({
      conversationId: conversation._id,
      tab: ChatDetailsTitle.MEDIA,
    });
  };

  const participants = useWorkspaceParticipantsUsers(conversation.participants);
  const users = useMemo(
    () => participants.filter((i) => i._id !== user?._id),
    [participants, user?._id],
  );
  const otherUser = useMemo(() => users[0], [users]);

  const voiceMember = useMemo(
    () => voiceMembers?.find((member) => member.user._id === otherUser?._id),
    [voiceMembers, otherUser?._id],
  );

  return (
    <NavItem
      depth={1}
      highlight={!!conversation.unreadMessages?.length}
      badgeContent={conversation.unreadMessages?.length}
      path={path}
      icon={<P2PChannelAvatar otherUser={otherUser} />}
      action={
        <NavActionButton
          onClick={handleShowDetails}
          icon="tabler:settings"
          size={16}
        />
      }
    >
      <StyledContainer>
        <span>{otherUser?.name}</span>
        {(voiceMember?.sharing || voiceMember?.camera) && (
          <Iconify
            className="call-icon"
            icon={voiceMember?.sharing ? 'tabler:screen-share' : 'tabler:video'}
          />
        )}
      </StyledContainer>
    </NavItem>
  );
};

const StyledContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  '& span': {
    ...theme.typography.body2,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '& .call-icon': {
    width: theme.spacing(2),
    color: theme.palette.success.main,
  },
}));
